/**
 * Road shipment section of the search form: less than truckload, parcel or
 * full truckload, each with only its own fields. Part of LaneSearchForm; its
 * values map to the neutral `road` block (roadRequest.ts). Summaries are
 * estimates for the user; the vendor prices the freight.
 */
import React, { useState } from 'react';
import { Checkbox, Form } from 'antd';

import { CustomInput, CustomSelect } from '../../../components';
import { integerRule, numberRule } from './formRules';
import {
  COMMON_ACCESSORIALS,
  EQUIPMENT_OPTIONS,
  FREIGHT_CLASSES,
  ltlSummary,
  MORE_ACCESSORIALS,
  PARCEL_SERVICE_OPTIONS,
  parcelSummary,
  ROAD_TEMP_OPTIONS,
  ROAD_TYPE_OPTIONS,
  RoadCargoValues,
  RoadType,
} from './roadRequest';
import styles from './styles.module.css';

const DIMENSIONS = [
  ['length', 'Length (cm)'],
  ['width', 'Width (cm)'],
  ['height', 'Height (cm)'],
] as const;

const Dimensions: React.FC = () => (
  <div className={`${styles.cargoRow} ${styles.cargoRowDims}`}>
    {DIMENSIONS.map(([name, label]) => (
      <Form.Item
        key={name}
        label={label}
        name={name}
        style={{ marginBottom: 12 }}
        rules={numberRule('Required')}
      >
        <CustomInput placeholder='cm' height='36px' />
      </Form.Item>
    ))}
  </div>
);

const SummaryRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className={styles.summaryRow}>
    <span className={styles.summaryLabel}>{label}</span>
    <span>{value}</span>
  </div>
);

const LtlFields: React.FC<{ values: RoadCargoValues; ltlOnly?: boolean }> = ({
  values,
  ltlOnly,
}) => {
  const [showMore, setShowMore] = useState(false);
  const summary = ltlSummary(values);
  const accessorials = showMore
    ? [...COMMON_ACCESSORIALS, ...MORE_ACCESSORIALS]
    : COMMON_ACCESSORIALS;
  return (
    <>
      {!ltlOnly && (
        <Form.Item
          label='Equipment'
          name='equipment'
          extra={
            values.equipment === 'Reefer'
              ? 'Reefer LTL is not quoted by your connected vendors: this search returns no Road rates.'
              : undefined
          }
        >
          <CustomSelect
            width='100%'
            height='36px'
            options={EQUIPMENT_OPTIONS}
          />
        </Form.Item>
      )}
      <div className={`${styles.cargoRow} ${styles.cargoRowTwo}`}>
        <Form.Item
          label='Pallets'
          name='pallets'
          style={{ marginBottom: 12 }}
          rules={integerRule('Whole number, min 1')}
        >
          <CustomInput placeholder='1' height='36px' />
        </Form.Item>
        <Form.Item
          label='Weight per pallet (kg)'
          name='weightPerPallet'
          style={{ marginBottom: 12 }}
          rules={numberRule('Required')}
        >
          <CustomInput placeholder='kg' height='36px' />
        </Form.Item>
      </div>
      <Dimensions />
      <div className={`${styles.cargoRow} ${styles.cargoRowTwo}`}>
        <Form.Item
          label='Freight class'
          name='freightClass'
          style={{ marginBottom: 12 }}
          rules={[{ required: true, message: 'Pick a freight class' }]}
          tooltip='The NMFC class sets the LTL rate. It follows density; the carrier may reclassify the shipment.'
          extra={
            summary
              ? `Suggested ${summary.estimatedClass} from the density below`
              : undefined
          }
        >
          <CustomSelect
            width='100%'
            height='36px'
            placeholder='Select'
            options={FREIGHT_CLASSES.map((value) => ({ value, label: value }))}
          />
        </Form.Item>
        <Form.Item
          label='NMFC code'
          name='nmfcCode'
          style={{ marginBottom: 12 }}
        >
          <CustomInput placeholder='e.g. 8460' height='36px' maxLength={32} />
        </Form.Item>
      </div>
      {summary && (
        <div className={styles.summaryBox}>
          <SummaryRow
            label='Total weight'
            value={`${Math.round(summary.totalWeightLb).toLocaleString()} lb`}
          />
          <SummaryRow
            label='Total volume'
            value={`${summary.totalVolumeFt3.toFixed(1)} ft³`}
          />
          <SummaryRow
            label='Calculated density'
            value={`${summary.densityLbFt3.toFixed(1)} lb/ft³`}
          />
          <SummaryRow
            label='Freight class'
            value={`${summary.estimatedClass} (estimated)`}
          />
        </div>
      )}
      <div className={styles.sectionTitleSub}>Accessorials</div>
      <Form.Item name='accessorials'>
        <Checkbox.Group>
          <div className={styles.checkboxStack}>
            {accessorials.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </div>
        </Checkbox.Group>
      </Form.Item>
      <button
        type='button'
        className={styles.addLink}
        aria-expanded={showMore}
        onClick={() => setShowMore((open) => !open)}
      >
        {showMore ? 'Fewer accessorials' : 'More accessorials'}
      </button>
    </>
  );
};

const ParcelFields: React.FC<{ values: RoadCargoValues }> = ({ values }) => {
  const summary = parcelSummary(values);
  return (
    <>
      <div className={`${styles.cargoRow} ${styles.cargoRowTwo}`}>
        <Form.Item
          label='Packages'
          name='packages'
          style={{ marginBottom: 12 }}
          rules={integerRule('Whole number, min 1')}
        >
          <CustomInput placeholder='1' height='36px' />
        </Form.Item>
        <Form.Item
          label='Service'
          name='parcelService'
          style={{ marginBottom: 12 }}
        >
          <CustomSelect
            width='100%'
            height='36px'
            placeholder='Any'
            allowClear
            options={PARCEL_SERVICE_OPTIONS}
          />
        </Form.Item>
      </div>
      <Form.Item
        label='Weight per package (kg)'
        name='weightPerPackage'
        style={{ marginBottom: 12 }}
        rules={numberRule('Required')}
      >
        <CustomInput placeholder='kg' height='36px' />
      </Form.Item>
      <Dimensions />
      <Form.Item name='residential' valuePropName='checked'>
        <Checkbox>Residential delivery</Checkbox>
      </Form.Item>
      {summary && (
        <div className={styles.summaryBox}>
          <SummaryRow
            label='Actual weight'
            value={`${summary.actualKg.toFixed(1)} kg / pkg`}
          />
          <SummaryRow
            label='Dim weight'
            value={`${summary.dimKg.toFixed(1)} kg / pkg`}
          />
          <SummaryRow
            label='Billable weight'
            value={`${summary.billableKg.toFixed(1)} kg / pkg`}
          />
        </div>
      )}
    </>
  );
};

const FtlFields: React.FC<{ values: RoadCargoValues }> = ({ values }) => (
  <>
    <div className={`${styles.cargoRow} ${styles.cargoRowTwo}`}>
      <Form.Item
        label='Equipment'
        name='equipment'
        style={{ marginBottom: 12 }}
      >
        <CustomSelect width='100%' height='36px' options={EQUIPMENT_OPTIONS} />
      </Form.Item>
      {values.equipment === 'Reefer' && (
        <Form.Item
          label='Temp range'
          name='tempRange'
          style={{ marginBottom: 12 }}
          rules={[{ required: true, message: 'Pick a temperature range' }]}
        >
          <CustomSelect
            width='100%'
            height='36px'
            placeholder='Select range'
            options={ROAD_TEMP_OPTIONS}
          />
        </Form.Item>
      )}
    </div>
    <div className={styles.checkboxStack}>
      <Form.Item name='teamDrivers' valuePropName='checked'>
        <Checkbox>Team drivers (expedited)</Checkbox>
      </Form.Item>
      <Form.Item name='droppedTrailer' valuePropName='checked'>
        <Checkbox>Dropped trailer</Checkbox>
      </Form.Item>
    </div>
  </>
);

const RoadCargoSection: React.FC<{
  title?: string;
  /** Pickup and delivery trucking: always dry-van LTL, so no type or equipment choice. */
  ltlOnly?: boolean;
}> = ({ title = 'Shipment', ltlOnly }) => {
  const form = Form.useFormInstance();
  const watched = Form.useWatch('roadType', form) as RoadType | undefined;
  const roadType = ltlOnly ? 'ltl' : watched || 'ltl';
  // Re-render the summaries as any road field changes.
  const values = (Form.useWatch([], form) ?? {}) as RoadCargoValues;

  return (
    <>
      <div className={styles.sectionTitle}>{title}</div>
      {!ltlOnly && (
        <Form.Item label='Type' name='roadType'>
          <CustomSelect
            width='100%'
            height='36px'
            options={ROAD_TYPE_OPTIONS}
          />
        </Form.Item>
      )}
      {roadType === 'ltl' && <LtlFields values={values} ltlOnly={ltlOnly} />}
      {roadType === 'parcel' && <ParcelFields values={values} />}
      {roadType === 'ftl' && <FtlFields values={values} />}
    </>
  );
};

export default RoadCargoSection;
